import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Loader2, Plus, Trash2, KeyRound, Webhook, Copy, Ban } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EVENTS = ['job.completed', 'issue.created', 'deviation.flagged'];

const Integrations = () => {
  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyName, setKeyName] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [hookUrl, setHookUrl] = useState('');
  const [hookEvents, setHookEvents] = useState(['job.completed']);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [k, w] = await Promise.all([axios.get(`${API}/api-keys`), axios.get(`${API}/webhooks`)]);
      setKeys(k.data || []); setHooks(w.data || []);
    } catch (e) { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createKey = async () => {
    if (!keyName.trim()) { toast.error('Name the key'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api-keys`, { name: keyName.trim() });
      setNewKey(r.data); setKeyName(''); toast.success('API key created — copy it now'); load();
    } catch (e) { toast.error('Failed'); } finally { setBusy(false); }
  };
  const revokeKey = async (id) => { try { await axios.post(`${API}/api-keys/${id}/revoke`); load(); } catch (e) { toast.error('Failed'); } };

  const createHook = async () => {
    if (!hookUrl.trim() || hookEvents.length === 0) { toast.error('URL and at least one event required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/webhooks`, { url: hookUrl.trim(), events: hookEvents });
      setHookUrl(''); setHookEvents(['job.completed']); toast.success('Webhook added'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add webhook'); } finally { setBusy(false); }
  };
  const deleteHook = async (id) => { try { await axios.delete(`${API}/webhooks/${id}`); load(); } catch (e) { toast.error('Failed'); } };

  const toggleEvent = (ev) => setHookEvents((p) => p.includes(ev) ? p.filter((x) => x !== ev) : [...p, ev]);

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">API & Webhooks</h1>
          <p className="text-slate-600 mt-1">Connect ReasonVisionX to your ERP / PM tools via the public API and event webhooks</p>
        </div>

        {loading ? <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div> : (
        <>
        {/* API Keys */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="api-keys-card">
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><KeyRound className="w-5 h-5 text-indigo-500" /> API Keys</h2>
          {newKey && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="new-api-key">
              <p className="text-xs text-amber-700 mb-1">Copy this key now — it won't be shown again:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm bg-white px-2 py-1 rounded border border-amber-200 break-all">{newKey.apiKey}</code>
                <button onClick={() => { navigator.clipboard.writeText(newKey.apiKey); toast.success('Copied'); }} className="p-2 bg-amber-600 text-white rounded" data-testid="copy-api-key"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
          )}
          <div className="flex gap-2 mb-4">
            <input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="Key name (e.g. Procore)" className="flex-1 px-3 py-2 border border-slate-300 rounded-lg" data-testid="api-key-name" />
            <button onClick={createKey} disabled={busy} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-60" data-testid="api-key-create"><Plus className="w-4 h-4" /> Generate</button>
          </div>
          {keys.length === 0 ? <p className="text-sm text-slate-400">No API keys yet.</p> : (
            <div className="space-y-2" data-testid="api-key-list">
              {keys.map((k) => (
                <div key={k.id} className="flex items-center justify-between border border-slate-200 rounded-lg p-3" data-testid="api-key-row">
                  <div><p className="font-medium text-slate-800">{k.name} {k.revoked && <span className="text-xs text-red-500">(revoked)</span>}</p><p className="text-xs font-mono text-slate-400">{k.prefix}…</p></div>
                  {!k.revoked && <button onClick={() => revokeKey(k.id)} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1" data-testid="api-key-revoke"><Ban className="w-3 h-3" /> Revoke</button>}
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-3">Use <span className="font-mono">X-API-Key</span> header against <span className="font-mono">/api/v1/projects</span>, <span className="font-mono">/api/v1/analysis-jobs</span>, <span className="font-mono">/api/v1/issues</span>, <span className="font-mono">/api/v1/cost-summary</span>.</p>
        </div>

        {/* Webhooks */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="webhooks-card">
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><Webhook className="w-5 h-5 text-teal-500" /> Webhooks</h2>
          <div className="space-y-3 mb-4">
            <input value={hookUrl} onChange={(e) => setHookUrl(e.target.value)} placeholder="https://your-endpoint.com/hooks" className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="webhook-url" />
            <div className="flex gap-2 flex-wrap">
              {EVENTS.map((ev) => (
                <button key={ev} onClick={() => toggleEvent(ev)} className={`px-3 py-1 rounded-full text-xs font-medium ${hookEvents.includes(ev) ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'}`} data-testid={`webhook-event-${ev}`}>{ev}</button>
              ))}
            </div>
            <button onClick={createHook} disabled={busy} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 disabled:opacity-60" data-testid="webhook-create"><Plus className="w-4 h-4" /> Add Webhook</button>
          </div>
          {hooks.length === 0 ? <p className="text-sm text-slate-400">No webhooks configured.</p> : (
            <div className="space-y-2" data-testid="webhook-list">
              {hooks.map((h) => (
                <div key={h.id} className="flex items-center justify-between border border-slate-200 rounded-lg p-3" data-testid="webhook-row">
                  <div className="min-w-0"><p className="font-medium text-slate-800 truncate">{h.url}</p><p className="text-xs text-slate-400">{h.events.join(', ')} · secret {h.secretHint || (h.secret ? h.secret.slice(0, 10) + '…' : '')}</p></div>
                  <button onClick={() => deleteHook(h.id)} className="text-slate-400 hover:text-red-500" data-testid="webhook-delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-3">Payloads are POSTed with an <span className="font-mono">X-Webhook-Signature: sha256=…</span> HMAC header (verify with the webhook secret). Delivery is background with retry.</p>
        </div>
        </>
        )}
      </div>
    </Layout>
  );
};

export default Integrations;
